import { SuggestedAction } from '../models';

/** Calendrier tel que l'assistant le propose (sous-ensemble de `Calendar`). */
export interface CalendarChoiceSource {
  id?: number | null;
  label: string;
}

/** Choix de calendrier prêt à afficher. */
export interface CalendarChoice {
  id: number;
  label: string;
  /** Dernier calendrier utilisé par la personne : proposé en premier, mis en avant. */
  isLast: boolean;
}

/** Mention affichée dans le bouton du dernier calendrier utilisé. */
export const LAST_CALENDAR_HINT = 'dernier utilisé';

/** Préfixe des identifiants de bouton (gabarit stable, un par calendrier). */
const CHOICE_ID_PREFIX = 'calendar-pick-';

/**
 * Calendriers à proposer : le dernier utilisé d'abord, les autres dans l'ordre
 * reçu (déjà triés par libellé côté profil). Les doublons d'identifiant et les
 * calendriers sans identifiant sont écartés ; un `lastCalendarId` qui n'est pas
 * dans la liste est ignoré (calendrier supprimé, autre organisation).
 */
export function orderCalendarChoices(
  calendars: readonly CalendarChoiceSource[],
  lastCalendarId: number | null
): CalendarChoice[] {
  const seen = new Set<number>();
  const choices: CalendarChoice[] = [];
  for (const c of calendars) {
    if (typeof c.id !== 'number' || seen.has(c.id)) continue;
    seen.add(c.id);
    choices.push({ id: c.id, label: c.label, isLast: c.id === lastCalendarId });
  }
  return [...choices.filter(c => c.isLast), ...choices.filter(c => !c.isLast)];
}

/**
 * Boutons « choisir le calendrier » : chacun rejoue l'intent d'origine avec
 * `calendarId`, donc l'action demandée s'exécute au clic sans que la personne
 * répète sa phrase. Seules les valeurs déjà validées de l'intent sont reprises.
 */
export function buildCalendarChoiceActions(
  endpoint: string,
  params: Record<string, unknown>,
  choices: readonly CalendarChoice[]
): SuggestedAction[] {
  return choices.map(c => ({
    id: `${CHOICE_ID_PREFIX}${c.id}`,
    label: c.label,
    endpoint,
    params: { ...params, calendarId: c.id },
    ...(c.isLast ? { emphasis: true, hint: LAST_CALENDAR_HINT } : {})
  }));
}

/** `calendarId` d'un intent rejoué : un entier strictement positif, sinon `null`. */
export function readCalendarId(params: Record<string, unknown>): number | null {
  const raw = params['calendarId'];
  const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
}

/** Lieu d'un calendrier qui n'est pas ouvert à l'écran (même règle que la page Calendrier). */
export function pickLocalisationFor<T extends { id?: number | null }>(
  localisations: readonly T[],
  storedPreferenceId: string | null
): T | null {
  if (localisations.length === 0) return null;
  if (localisations.length === 1) return localisations[0];
  return localisations.find(l => l.id !== undefined && String(l.id) === storedPreferenceId) ?? localisations[0];
}
